import { createReducer } from '@reduxjs/toolkit'
import { isUndefined, uniq } from 'underscore'
import { types } from '../../actions/elements_order'

const initialState = {
  currentElementGroup: null,
  currentDepartment: null,
  currentElementSubGroup: null,
  currentTemplate: null,
  currentTemplateData: null,
  currentReplaceElementId: null,
  currentElement: null,
  searchTerm: null,
  templates: [],
  templatesLoading: false,
  installedElements: [],
  installedElementsLoading: false,
  elements: {},
  removed: [],
  filtered: {
    elementGroups: [],
    departments: [],
    elementSubGroups: [],
  },
  dictionaries: {
    elementGroups: [],
    departments: [],
    elementSubGroups: [],
    locationGroups: [],
    locationSubGroups: [],
    locationDepartments: [],
  },
  companySettings: {
    allowProjectCreation: false,
    allowImageUpload: false,
  },
  uploaderOptions: {}
}

export default createReducer(initialState, (builder) => {
  builder
    .addCase(types.LOAD_ELEMENTS, (state, action: any) => {
      state.elements = action.elements
    })
    .addCase(types.LOAD_DICTIONARIES, (state, action: any) => {
      state.dictionaries = action.dictionaries
      state.companySettings = action.companySettings
      state.uploaderOptions = action.uploaderOptions
    })
    .addCase(types.LOAD_TEMPLATES_REQUEST, (state) => {
      state.templatesLoading = true
      state.currentReplaceElementId = null
      state.currentTemplateData = null
      state.currentTemplate = null
      state.currentElement = null
    })
    .addCase(types.LOAD_TEMPLATES_FAILURE, (state) => {
      state.templatesLoading = false
    })
    .addCase(types.LOAD_TEMPLATES, (state, action: any) => {
      const templates = action.data
      const allSubGroups = []
      const allDepartments = []

      templates.forEach((t) => {
        if (!isUndefined(t.fields.sub_group)) {
          const subGroupId = parseInt(t.fields.sub_group.default_value, 10)
          allSubGroups.push(subGroupId)
        }
        if (!isUndefined(t.fields.department)) {
          const departmentId = parseInt(t.fields.department.default_value, 10)
          allDepartments.push(departmentId)
        }
      })

      let filteredSubGroups = state.filtered.elementSubGroups
      if (!state.currentElementSubGroup) {
        filteredSubGroups = uniq(allSubGroups)
      }
      let filteredDepartments = state.filtered.departments
      if (!state.currentDepartment) {
        filteredDepartments = uniq(allDepartments)
      }

      state.templates = templates
      state.templatesLoading = false
      state.filtered.elementSubGroups = filteredSubGroups
      state.filtered.departments = filteredDepartments
    })
    .addCase(types.LOAD_TEMPLATE_REQUEST, (state, action: any) => {
      state.currentTemplate = Number(action.templateId)
      state.currentReplaceElementId = null
      state.currentTemplateData = null
    })
    .addCase(types.LOAD_TEMPLATE, (state, action: any) => {
      state.currentTemplateData = action.data
      state.currentElement = null
    })
    .addCase(types.LOAD_TEMPLATE_FAILURE, (state) => {
      state.currentTemplateData = null
      state.currentElement = null
    })
    .addCase(types.LOAD_INSTALLED_ELEMENTS_REQUEST, (state) => {
      state.installedElementsLoading = true
    })
    .addCase(types.LOAD_INSTALLED_ELEMENTS_FAILURE, (state) => {
      state.installedElementsLoading = false
    })
    .addCase(types.LOAD_INSTALLED_ELEMENTS, (state, action: any) => {
      const installedElements = action.data
      const allSubGroups = []
      const allDepartments = []

      installedElements.forEach((t) => {
        if (!isUndefined(t.sub_group_id)) {
          const subGroupId = parseInt(t.sub_group_id, 10)
          allSubGroups.push(subGroupId)
        }
        if (!isUndefined(t.department_id)) {
          const departmentId = parseInt(t.department_id, 10)
          allDepartments.push(departmentId)
        }
      })

      let filteredSubGroups = state.filtered.elementSubGroups
      if (!state.currentElementSubGroup) {
        filteredSubGroups = uniq(allSubGroups)
      }
      let filteredDepartments = state.filtered.departments
      if (!state.currentDepartment) {
        filteredDepartments = uniq(allDepartments)
      }

      state.installedElements = installedElements
      state.installedElementsLoading = false
      state.filtered.elementSubGroups = filteredSubGroups
      state.filtered.departments = filteredDepartments
    })
    .addCase(types.LOAD_INSTALLED_ELEMENT, (state, action: any) => {
      state.currentTemplateData = action.data
      state.currentElement = null
      state.currentTemplate = Number(action.data.id)
    })
    .addCase(types.LOAD_INSTALLED_ELEMENT_REQUEST, (state, action: any) => {
      state.currentReplaceElementId = Number(action.elementId)
      state.currentTemplateData = null
    })
    .addCase(types.LOAD_INSTALLED_ELEMENT_FAILURE, (state) => {
      state.currentTemplateData = null
      state.currentElement = null
    })
    .addCase(types.SELECT_ELEMENT_GROUP, (state, action: any) => {
      state.currentElementGroup = Number(action.elementGroupId)
      state.currentElementSubGroup = null
      state.currentDepartment = null
    })
    .addCase(types.SELECT_ELEMENT_SUB_GROUP, (state, action: any) => {
      state.currentElementSubGroup = Number(action.elementSubGroupId)
      state.currentDepartment = null
    })
    .addCase(types.SELECT_DEPARTMENT, (state, action: any) => {
      state.currentDepartment = Number(action.departmentId)
    })
    .addCase(types.SEARCH_TEMPLATE, (state, action: any) => {
      state.searchTerm = action.searchTerm
    })
    .addCase(types.ADD_ELEMENT, (state, action: any) => {
      state.elements[action.key.toString()] = action.element
      state.currentTemplateData = null
      state.currentTemplate = null
      state.currentElement = null
    })
    .addCase(types.UPDATE_ELEMENT, (state, action: any) => {
      const elements = Object.values(state.elements)
      const element = elements.find(el => el[action.finderKey] == action.finderValue)
      if (element) {
        element[action.field] = action.value
      }
    })
    .addCase(types.EDIT_ELEMENT, (state, action: any) => {
      state.currentTemplateData = null
      state.currentTemplate = null
      state.currentElement = action.element
      state.currentReplaceElementId = Number(action.element.replace_element_id || null)
    })
    .addCase(types.REMOVE_ELEMENT, (state, action: any) => {
      delete state.elements[action.key.toString()]
      if (action.id) {
        state.removed.unshift(action.id)
      }
    })
    .addCase(types.CLEAR_FORM, (state) => {
      const elements = Object.values(state.elements)
      elements.forEach((e: any) => {
        if (e.id) {
          state.removed.unshift(e.id)
        }
      })
      state.elements = {}
    })
})